import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRinging, Trash } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/replenishment-settings", label: "Replenishment Alerts", icon: <BellRinging size={18} weight="duotone" />, testId: "nav-admin-replenishment-settings" }];

export default function AdminReplenishmentSettingsPage() {
  const confirmEdit = useEditConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thresholdDraft, setThresholdDraft] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [overrideRep, setOverrideRep] = useState("");
  const [overrideThreshold, setOverrideThreshold] = useState("");
  const [history, setHistory] = useState([]);
  const [testing, setTesting] = useState(false);

  const [managerThresholdDraft, setManagerThresholdDraft] = useState("");
  const [savingManagerThreshold, setSavingManagerThreshold] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/replenishment-settings"), api.get("/admin/replenishment-history")])
      .then(([settingsRes, historyRes]) => {
        setData(settingsRes.data);
        setThresholdDraft(String(settingsRes.data.global_threshold));
        setManagerThresholdDraft(String(settingsRes.data.manager_low_lead_threshold));
        setHistory(historyRes.data || []);
      })
      .catch(() => toast.error("Couldn't load replenishment settings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const saveThreshold = async () => {
    const value = parseInt(thresholdDraft, 10);
    if (!value || value < 1) {
      toast.error("Enter a valid threshold");
      return;
    }
    if (!(await confirmEdit("the Global Lead Threshold"))) return;
    setSavingThreshold(true);
    try {
      const { data: res } = await api.put("/admin/replenishment-settings/global-threshold", { threshold: value });
      toast.success(res.message);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingThreshold(false);
    }
  };

  const toggleDigest = async (enabled) => {
    if (!(await confirmEdit(`Daily Digest Mode (turn ${enabled ? "ON" : "OFF"})`))) return;
    try {
      const { data: res } = await api.put("/admin/replenishment-settings/digest-mode", { enabled });
      toast.success(res.message);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addOverride = async () => {
    const value = parseInt(overrideThreshold, 10);
    if (!overrideRep || !value || value < 1) {
      toast.error("Pick a rep and a valid threshold");
      return;
    }
    const rep = data.reps.find((r) => r.rep_id === overrideRep);
    if (!(await confirmEdit(`${rep?.rep_name || "this rep"}'s threshold override`))) return;
    try {
      const { data: res } = await api.put("/admin/replenishment-settings/rep-override", { rep_id: overrideRep, threshold: value });
      toast.success(res.message);
      setOverrideRep("");
      setOverrideThreshold("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveManagerThreshold = async () => {
    const value = parseInt(managerThresholdDraft, 10);
    if (!value || value < 1) {
      toast.error("Enter a valid threshold");
      return;
    }
    if (!(await confirmEdit("Vince's (VP001) Low-Lead Alert Threshold"))) return;
    setSavingManagerThreshold(true);
    try {
      const { data: res } = await api.put("/admin/replenishment-settings/manager-low-lead-threshold", { threshold: value });
      toast.success(res.message);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingManagerThreshold(false);
    }
  };

  const removeOverride = async (repId) => {
    try {
      await api.delete(`/admin/replenishment-settings/rep-override/${repId}`);
      toast.success("Override removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendTestAlert = async () => {
    setTesting(true);
    try {
      const { data: res } = await api.post("/admin/replenishment-settings/send-test-alert");
      if (res.sent) toast.success(`Alert sent — ${res.entries.length} rep+category combo(s) low right now.`);
      else toast.info(res.message);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const [clearingPending, setClearingPending] = useState(false);
  const clearPendingAlert = async () => {
    setClearingPending(true);
    try {
      const { data: res } = await api.post("/admin/replenishment-settings/clear-pending");
      if (res.cleared) toast.success(res.message);
      else toast.info(res.message);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setClearingPending(false);
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Replenishment Alerts" moduleBackPath="/admin">
      <div data-testid="admin-replenishment-settings-page" className="max-w-4xl">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Controls the automatic WhatsApp alerts that fire when a rep is running low on assigned leads.">WhatsApp Replenishment Settings</h1>
        <p className="text-igc-muted text-sm mb-6">
          One aggregated WhatsApp alert covers every rep low on leads — reply REPLENISH ALL or REPLENISH [rep code] to authorize. Nothing pulls without your reply.
        </p>

        {loading && <p className="text-igc-muted text-sm" data-testid="replenishment-settings-loading">Loading...</p>}

        {!loading && data && (
          <div className="space-y-6">
            <div className="bg-igc-surface border border-igc-gold/15 p-6" data-testid="replenishment-global-threshold-card">
              <label className="text-xs uppercase tracking-widest text-igc-muted block mb-2">Global Lead Threshold</label>
              <div className="flex gap-3 items-center">
                <Input
                  data-testid="replenishment-global-threshold-input"
                  type="number"
                  value={thresholdDraft}
                  onChange={(e) => setThresholdDraft(e.target.value)}
                  title="Any rep whose trust or funding leads drop to or below this number gets flagged for a replenishment alert."
                  className="w-32 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"
                />
                <Button data-testid="replenishment-global-threshold-save" onClick={saveThreshold} disabled={savingThreshold} title="Saves this threshold for every rep without a personal override." className="bg-igc-gold hover:bg-igc-goldHover text-black">
                  {savingThreshold ? "Saving..." : "Save"}
                </Button>
              </div>
              <p className="text-igc-muted text-xs mt-2">Any rep at or below this many trust OR funding leads gets flagged in the next alert.</p>
            </div>

            <div className="bg-igc-surface border border-igc-gold/15 p-6 flex items-center justify-between" data-testid="replenishment-digest-mode-card">
              <div>
                <p className="text-igc-cream text-sm font-medium">Daily Digest Mode</p>
                <p className="text-igc-muted text-xs mt-1">ON: one 7 AM ET WhatsApp summary instead of instant threshold alerts throughout the day. Default: OFF.</p>
              </div>
              <Switch data-testid="replenishment-digest-mode-toggle" checked={data.digest_mode} onCheckedChange={toggleDigest} title="Switches between one daily 7 AM summary alert and instant alerts throughout the day." />
            </div>

            <div className="bg-igc-surface border border-igc-gold/15 p-6" data-testid="manager-low-lead-threshold-card">
              <label className="text-xs uppercase tracking-widest text-igc-muted block mb-2">Manager (Vince/VP001) Low-Lead Alert Threshold</label>
              <div className="flex gap-3 items-center">
                <Input
                  data-testid="manager-low-lead-threshold-input"
                  type="number"
                  value={managerThresholdDraft}
                  onChange={(e) => setManagerThresholdDraft(e.target.value)}
                  title="Any rep whose trust or funding leads drop below this number sends Vince a per-rep WhatsApp alert (max once per rep per day, 7am-9pm ET only)."
                  className="w-32 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"
                />
                <Button data-testid="manager-low-lead-threshold-save" onClick={saveManagerThreshold} disabled={savingManagerThreshold} title="Saves this threshold for Vince's per-rep low-lead alert." className="bg-igc-gold hover:bg-igc-goldHover text-black">
                  {savingManagerThreshold ? "Saving..." : "Save"}
                </Button>
              </div>
              <p className="text-igc-muted text-xs mt-2">Separate from the Global Lead Threshold above — this one is Vince-only, per rep, once a day, 7am-9pm ET.</p>
            </div>

            {data.pending_alert && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 text-amber-300 text-sm flex items-center justify-between gap-4 flex-wrap" data-testid="replenishment-pending-alert-banner">
                <span>A replenishment alert is currently pending Wes's WhatsApp reply — {data.pending_alert.entries?.length || 0} rep+category combo(s) flagged{data.pending_alert.created_at ? ` (sent ${formatET(data.pending_alert.created_at, "MMM d, h:mm a")} ET)` : ""}.</span>
                <Button
                  data-testid="replenishment-clear-pending-button"
                  onClick={clearPendingAlert}
                  disabled={clearingPending}
                  size="sm"
                  title="Clears this pending alert without waiting for a WhatsApp reply."
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40"
                >
                  {clearingPending ? "Clearing..." : "Clear Pending Alert"}
                </Button>
              </div>
            )}

            <div className="bg-igc-surface border border-igc-gold/15 p-6" data-testid="replenishment-overrides-card">
              <label className="text-xs uppercase tracking-widest text-igc-muted block mb-3">Per-Rep Threshold Overrides</label>
              <div className="flex gap-3 items-center mb-4">
                <Select value={overrideRep} onValueChange={setOverrideRep}>
                  <SelectTrigger data-testid="replenishment-override-rep-select" title="Pick a rep to give their own personal threshold, different from the global setting." className="w-56 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue placeholder="Select rep..." />
                  </SelectTrigger>
                  <SelectContent>
                    {data.reps.map((r) => (
                      <SelectItem key={r.rep_id} value={r.rep_id}>{r.rep_code} — {r.rep_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="replenishment-override-threshold-input"
                  type="number"
                  placeholder="Threshold"
                  value={overrideThreshold}
                  onChange={(e) => setOverrideThreshold(e.target.value)}
                  title="This rep's personal lead threshold, overriding the global setting."
                  className="w-32 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"
                />
                <Button data-testid="replenishment-override-add-button" onClick={addOverride} title="Saves this rep's custom threshold." className="bg-igc-gold hover:bg-igc-goldHover text-black">Add Override</Button>
              </div>
              {data.overrides.length === 0 ? (
                <p className="text-igc-muted text-xs" data-testid="replenishment-overrides-empty">No per-rep overrides — everyone uses the global threshold.</p>
              ) : (
                <div className="space-y-2">
                  {data.overrides.map((o) => (
                    <div key={o.rep_id} className="flex items-center justify-between bg-igc-surfaceLight px-3 py-2" data-testid={`replenishment-override-row-${o.rep_id}`}>
                      <span className="text-igc-cream text-sm">{o.rep_code} — {o.rep_name}: {o.threshold} leads</span>
                      <button data-testid={`replenishment-override-remove-${o.rep_id}`} onClick={() => removeOverride(o.rep_id)} title="Removes this rep's custom threshold — they'll go back to using the global setting." className="text-igc-danger hover:text-igc-danger/70">
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Button data-testid="replenishment-send-test-alert-button" onClick={sendTestAlert} disabled={testing} title="Immediately checks every rep against their threshold and sends a WhatsApp alert if anyone is low." className="bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">
                {testing ? "Checking..." : "Check Now & Send Alert If Needed"}
              </Button>
            </div>

            <div className="bg-igc-surface border border-igc-gold/15 p-6" data-testid="replenishment-history-card">
              <label className="text-xs uppercase tracking-widest text-igc-muted block mb-3">Replenishment History</label>
              {history.length === 0 ? (
                <p className="text-igc-muted text-xs" data-testid="replenishment-history-empty">No replenishments logged yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs whitespace-nowrap" data-testid="replenishment-history-table">
                    <thead>
                      <tr className="text-igc-muted uppercase tracking-wide text-left">
                        <th className="pb-2 pr-4">Rep</th>
                        <th className="pb-2 pr-4">Category</th>
                        <th className="pb-2 pr-4">Result</th>
                        <th className="pb-2 pr-4">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="border-t border-igc-gold/10 text-igc-cream" data-testid={`replenishment-history-row-${h.id}`}>
                          <td className="py-2 pr-4">{h.rep_code} {h.rep_name}</td>
                          <td className="py-2 pr-4 capitalize">{h.category}</td>
                          <td className="py-2 pr-4">{h.outcome?.success ? `+${h.outcome.added} → ${h.outcome.new_total}` : `FAILED: ${h.outcome?.error || "unknown"}`}</td>
                          <td className="py-2 pr-4">{formatET(h.completed_at, "MMM d, h:mm a")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
